import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import * as Fonts from "../../components/Fonts";
import { Box } from "../../components/Layout";
import { Skeleton } from "../../components/Skeleton";
import { SubHeader } from "../../components/SubHeader";
import { useToast } from "../../components/Toast";
import { useAppState } from "../../contexts/App";
import { Contact, ContactField, getContact } from "../../services/contacts";
import WhatsappIcon from "../Editor/pages/Sections/icons/Whatsapp";
import { CopyIcon } from "../Links/components/LinkCard";
import { CopyButton } from "../Links/components/LinkCard/styles";
import { contactDate, emailOf, isEmailField, isPhoneField, isSafeEmail, phoneOf, whatsappLink } from "./helpers";
import { DataRows, LoadingScreen, Message, Note, Overview, ReplyButton, Section } from "./styles";

const prefix = "pages.contacts";

const isMessageField = (field: ContactField) => field.type === "LONG_TEXT" || field.key === "message";

/** A field's value: an e-mail and a phone are links to answer the person. */
const FieldValue = ({ field }: { field: ContactField }) => {
  if (isEmailField(field) && isSafeEmail(field.value)) {
    return <a href={`mailto:${field.value}`}>{field.value}</a>;
  }
  const whatsapp = isPhoneField(field) ? whatsappLink(field.value) : null;
  if (whatsapp) {
    return (
      <a href={whatsapp} target="_blank" rel="noopener noreferrer">
        {field.value}
      </a>
    );
  }
  return <>{field.value}</>;
};

/**
 * One contact from the form, as a domain's screen: who sent it and when, with the ways to answer on top; what they
 * wrote in a frame of its own; and the rest of what they filled in, a row each, ready to copy.
 */
const ContactDetailPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { contactId = "" } = useParams();
  const { appState } = useAppState();
  const siteId = appState.editingSiteId ?? "";
  const [contact, setContact] = useState<Contact>();
  const [error, setError] = useState<"notFound" | "loadError">();

  const load = useCallback(() => {
    if (!siteId) return;
    let current = true;
    // another contact (or Keepo): never the one before while it loads
    setContact(undefined);
    setError(undefined);
    getContact(siteId, contactId)
      .then(found => current && setContact(found))
      .catch(e => current && setError(e?.response?.status === 404 ? "notFound" : "loadError"));
    return () => {
      current = false;
    };
  }, [siteId, contactId]);

  useEffect(load, [load]);

  // A field without a label (or an old contact's fixed one) is named by what it is
  const labelOf = (field: ContactField) =>
    field.label || (field.key ? t(`${prefix}.fields.${field.key}`) : t(`${prefix}.types.${field.type}`, field.type));

  const copy = async (field: ContactField) => {
    try {
      await navigator.clipboard.writeText(field.value);
      toast.info(t(`${prefix}.copied`), { description: field.value });
    } catch {
      toast.error(t(`${prefix}.copyError`));
    }
  };

  const title = contact?.title || t(`${prefix}.untitled`);
  const message = contact?.fields.find(isMessageField);
  const data = contact?.fields.filter(field => field !== message) ?? [];
  const email = contact && emailOf(contact);
  const phone = contact && phoneOf(contact);
  const whatsapp = phone ? whatsappLink(phone) : null;

  return (
    <>
      <SubHeader
        title={contact ? title : t(`${prefix}.contact`)}
        crumbs={[{ label: t(`${prefix}.title`), to: "/contacts" }]}
      />
      <Box>
        {!contact && !error && (
          <LoadingScreen role="status" aria-label={t(`${prefix}.loading`)}>
            <Skeleton className="card" />
            <Skeleton className="card" />
          </LoadingScreen>
        )}

        {error && (
          <Note role="alert">
            {t(`${prefix}.${error}`)}{" "}
            {error === "loadError" && (
              <button type="button" onClick={load}>
                {t(`${prefix}.retry`)}
              </button>
            )}
          </Note>
        )}

        {contact && (
          <>
            <Overview>
              <span className="initial" aria-hidden="true">
                {title.trim().charAt(0).toUpperCase()}
              </span>
              <div className="who">
                <h3>{title}</h3>
                {contact.createdAt && (
                  <p>{t(`${prefix}.receivedAt`, { date: contactDate(contact.createdAt, i18n.language) })}</p>
                )}
              </div>
              {(whatsapp || email) && (
                <div className="answer">
                  {whatsapp && (
                    <ReplyButton href={whatsapp} target="_blank" rel="noopener noreferrer" $primary>
                      <WhatsappIcon width={18} height={18} />
                      {t(`${prefix}.replyWhatsapp`)}
                    </ReplyButton>
                  )}
                  {email && <ReplyButton href={`mailto:${email}`}>{t(`${prefix}.replyEmail`)}</ReplyButton>}
                </div>
              )}
            </Overview>

            {message && (
              <Section>
                <Fonts.Label>{t(`${prefix}.message`)}</Fonts.Label>
                <Message>{message.value}</Message>
              </Section>
            )}

            {(data.length > 0 || !message) && (
              <Section>
                <Fonts.Label>{t(`${prefix}.data`)}</Fonts.Label>
                <DataRows>
                  {contact.fields.length === 0 && <p className="empty">{t(`${prefix}.noFields`)}</p>}
                  {data.map((field, index) => (
                    <div key={index} className="row">
                      <dl>
                        <dt>{labelOf(field)}</dt>
                        <dd>
                          <FieldValue field={field} />
                        </dd>
                      </dl>
                      <CopyButton
                        type="button"
                        aria-label={t(`${prefix}.copy`, { label: labelOf(field) })}
                        title={t(`${prefix}.copy`, { label: labelOf(field) })}
                        onClick={() => copy(field)}>
                        <CopyIcon />
                      </CopyButton>
                    </div>
                  ))}
                </DataRows>
              </Section>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default ContactDetailPage;
